'use client';

import { ComponentPropsWithoutRef, Ref, useId } from 'react';
import * as Headless from '@headlessui/react';
import { LayoutGroup, motion } from 'motion/react';
import clsx from 'clsx';

import { TouchTarget } from './button-tw';
import { Link } from './link';
import { TActionItem } from './type';

/**
 * -----------------------------------------------------------------------------
 * The OG navbar component that renders the container navigation elements at the
 * top of the screen.
 * TODO: Update the theming support on these elements.
 */
export function Navbar({
  className,
  ...props
}: ComponentPropsWithoutRef<'nav'>) {
  return (
    <nav
      {...props}
      className={clsx(className, 'flex flex-1 items-center gap-4 py-2.5')}
    />
  );
}

/**
 * -----------------------------------------------------------------------------
 * This creates a thin line divider within the navbar content.
 */
export function NavbarDivider({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, 'h-6 w-px bg-gray-950/10 dark:bg-white/10')}
    />
  );
}

/**
 * -----------------------------------------------------------------------------
 * This creates a section grouping or related navbar items.
 */
export function NavbarSection({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'flex items-center gap-3')} />
    </LayoutGroup>
  );
}

/**
 * -----------------------------------------------------------------------------
 * Creates an empty div with styles to flexible spacing between the navbar
 * items, sections or dividers.
 */
export function NavbarSpacer({
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, '-ml-4 flex-1')}
    />
  );
}

/**
 * -----------------------------------------------------------------------------
 * This renders a single item of the navbar component.
 */
export function NavbarItem({
  current,
  className,
  children,
  ref,
  ...props
}: TActionItem) {
  const classes = clsx(
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-start text-base/6',
    'text-gray-950 sm:text-sm/5',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0',
    '*:data-[slot=icon]:fill-gray-500 sm:*:data-[slot=icon]:size-5',
    // Trailing icon (down chevron or similar)
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5',
    'sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7',
    '*:data-[slot=avatar]:[--avatar-radius:var(--radius-md)]',
    '*:data-[slot=avatar]:[--ring-opacity:10%] sm:*:data-[slot=avatar]:size-6',
    // Hover
    'data-hover:bg-gray-950/5 data-hover:*:data-[slot=icon]:fill-gray-950',
    // Active
    'data-active:bg-gray-950/5 data-active:*:data-[slot=icon]:fill-gray-950',
    // Dark mode
    'dark:text-white dark:*:data-[slot=icon]:fill-gray-400',
    'dark:data-hover:bg-white/5 dark:data-hover:*:data-[slot=icon]:fill-white',
    'dark:data-active:bg-white/5 dark:data-active:*:data-[slot=icon]:fill-white'
  );

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          className="
            absolute inset-x-2 -bottom-2.5 h-0.5 rounded-full bg-gray-950
            dark:bg-white"
          layoutId="current-indicator"
        />
      )}
      {'href' in props ? (
        <Link
          {...props}
          ref={ref as Ref<HTMLAnchorElement>}
          className={classes}
          data-current={current ? 'true' : undefined}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          ref={ref}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
}

/**
 * -----------------------------------------------------------------------------
 * This creates a simple none interactive label on the sidebar.
 */
export function NavbarLabel({
  className,
  ...props
}: ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />;
}
